import { useMemo } from 'react';
import { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import { CallRecordEventType } from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/enums.pb';
import {
  CallRecord,
  CallRecording,
  CallRecordTagNotification,
  CallRecordUpdateEvent,
} from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/message.pb';
import { InfiniteData, QueryClient, useQueryClient } from 'react-query';
import { PhoneCallsApi } from '@frontend/api-phone-calls';
import { TagsV2 } from '@frontend/api-tag';
import { useCallInsightController } from '@frontend/call-insight-panel';
import { RecentCallRecords } from '@frontend/phone-panels';
import { useAppScopeStore } from '@frontend/scope';
import { useVoiceAIRCustomizationFlag } from '@frontend/shared';
import {
  AllCallsTable,
  CallRecordsFiltersType,
  usePhonePageSettingShallowStore,
} from '../../hooks/use-phone-config-state-store';
import { queryKeys } from '../../query-keys';
import { formatEndDateWithTimeZone, formatStartDateWithTimeZone } from '../../utils';
import { getRecentCallsSearchQuery } from './utils';

const CALL_INTEL_UPDATE_EVENT_TYPES = [
  CallRecordEventType.CALL_RECORD_EVENT_TYPE_CALL_INTELLIGENCE_FINISHED_PROCESSING,
  CallRecordEventType.CALL_RECORD_EVENT_TYPE_CALL_INTELLIGENCE_NEW,
];

// updates the recent call records when a new call record is added or a call recording is finished processing
export const useCallRecordEvent = () => {
  const queryClient = useQueryClient();
  const { selectedOrgId, selectedLocationIds } = useAppScopeStore();
  const { config: phonePageConfig } = usePhonePageSettingShallowStore('config');
  const { updateCallInsights } = useCallInsightController({ source: 'callHistory' });
  const { locationFlagValueMap } = useVoiceAIRCustomizationFlag(selectedLocationIds);

  const pageSize = phonePageConfig[AllCallsTable.RecentCalls].pageSize;
  const filters = phonePageConfig[AllCallsTable.RecentCalls].filters;

  const { data: mappedTags = {} } = TagsV2.Queries.useListTagsQuery({
    request: { orgId: selectedOrgId, groupIds: selectedLocationIds },
    options: {
      select: (data) => {
        return data?.tags.reduce<Record<string, Tag>>((acc, tag) => {
          acc[tag.id] = tag;
          return acc;
        }, {});
      },
    },
  });

  const searchQuery = useMemo(
    () => getRecentCallsSearchQuery(filters, pageSize, mappedTags),
    [filters, pageSize, mappedTags]
  );

  const qnKeys = [selectedLocationIds, ...queryKeys.recentCallLogs(searchQuery)];

  const handleCallRecordEvent = (event: CallRecordUpdateEvent) => {
    if (event.type == CallRecordEventType.CALL_RECORD_EVENT_TYPE_NEW && event.callRecord) {
      addCallRecordToRecentCallRecords(
        event.callRecord,
        filters,
        queryClient,
        qnKeys,
        locationFlagValueMap[event.callRecord.locationId] ?? false
      );
    } else if (
      event.type == CallRecordEventType.CALL_RECORD_EVENT_TYPE_RECORDING_FINISHED_PROCESSING &&
      event.callRecording
    ) {
      updateCallRecordingInRecentCallRecords(event.callRecording, queryClient, qnKeys);
    } else if (CALL_INTEL_UPDATE_EVENT_TYPES.includes(event.type) && event.callIntelInsight) {
      updateCallIntelInsightInCache(event.callIntelInsight, queryClient, qnKeys);
      if (event.callIntelInsight.channelId) {
        updateCallInsights({ ...event.callIntelInsight, id: event.callIntelInsight.channelId });
        queryClient.invalidateQueries({ predicate: ({ queryKey }) => queryKey.includes('GetCallMetadata') });
      }
    } else if (event.type === CallRecordEventType.CALL_RECORD_EVENT_TYPE_FOLLOWUP_TASKS_PROCESSED && event.tasksCount) {
      updateFollowupTaskCountInCache(event.tasksCount, queryClient, qnKeys);
      queryClient.invalidateQueries({ predicate: ({ queryKey }) => queryKey.includes('ListTasks') });
    }
  };

  const handleCallRecordTagEvent = (event: CallRecordTagNotification) => {
    const { channelId, tagsPayload } = event;
    const tagIds = tagsPayload.map((tag) => tag.id);
    updateCallRecordTagsInRecentCalls({
      channelId,
      tagIds,
      mappedTags,
      queryClient,
      recentCallRecordsQueryKey: qnKeys,
    });
  };

  return { handleCallRecordEvent, handleCallRecordTagEvent };
};

export async function addCallRecordToRecentCallRecords(
  callRecord: CallRecord,
  appliedFilter: CallRecordsFiltersType | undefined,
  queryClient: QueryClient,
  recentCallRecordsQueryKey: (string | string[] | undefined)[],
  hasVoiceAi: boolean
) {
  if (!doesCallRecordPassFilterChecks(callRecord, appliedFilter)) {
    // if the call record does not pass the filter checks, ignore the event
    return;
  }
  const callRecordData = await queryClient.fetchQuery({
    queryKey: [callRecord?.locationId, callRecord?.channelId],
    queryFn: () =>
      PhoneCallsApi.getCallRecord({
        callRecordId: callRecord.id,
        channelId: callRecord.channelId,
        locationId: callRecord.locationId,
        // we are passing startTime, endTime for efficient lookup of call records in the backend
        startTime: callRecord.startedAt,
        endTime: callRecord.startedAt,
        hydrationFlags: {
          includePersonInfo: true,
          includePhoneTreeData: true,
          includeUserInfo: true,
          includeDownloadCallRecordingToken: true,
          includeAiCallCreditsDetail: hasVoiceAi,
        },
      }),
  });
  if (!callRecordData) {
    return;
  }
  const oldData = queryClient.getQueryData(recentCallRecordsQueryKey) as InfiniteData<RecentCallRecords>;
  if (!oldData) {
    console.error(
      'call records realtime update failed. unable to fetch recent calls data from query cache ',
      recentCallRecordsQueryKey
    );
    return;
  }

  const newData = { ...oldData };

  if (newData.pages.length === 0) {
    // if there are no pages in the call records, then add a new page
    newData.pages.push({
      data: [],
      rows: [],
      meta: {
        lastId: undefined,
        endDate: undefined,
        hasNext: false,
      },
    });
  }
  const newRecord = {
    ...callRecordData.record,
    isRecordingBeingProcessed: callRecord.isCallRecorded,
    tags: [],
  };
  newData.pages[0].data.unshift(newRecord);
  newData.pages[0].rows.unshift(newRecord);

  queryClient.setQueryData(recentCallRecordsQueryKey, newData);
}

export function updateCallRecordingInRecentCallRecords(
  recordingInfo: CallRecording,
  queryClient: QueryClient,
  recentCallRecordsQueryKey: (string | string[] | undefined)[]
) {
  const callRecords = queryClient.getQueryData(recentCallRecordsQueryKey) as InfiniteData<RecentCallRecords>;
  if (!callRecords) {
    return;
  }
  // try to find the call record in the first page of call records. If not found, ignore the event
  const callRecordIndex = callRecords.pages[0].data.findIndex((record) => record.channelId == recordingInfo.channelId);
  if (callRecordIndex === -1) {
    return;
  }

  const callRecord = callRecords.pages[0].data[callRecordIndex];
  const updatedCallRecord = {
    ...callRecord,
    isRecordingBeingProcessed: false,
    recordingFilename: recordingInfo.recordingFilename,
    recordingPath: recordingInfo.recordingPath,
  };

  const newData = [
    ...callRecords.pages[0].data.slice(0, callRecordIndex),
    updatedCallRecord,
    ...callRecords.pages[0].data.slice(callRecordIndex + 1),
  ];

  const newPage = {
    ...callRecords.pages[0],
    data: newData,
    rows: newData,
    meta: callRecords.pages[0].meta,
  };

  const newRecords = {
    ...callRecords,
    pages: [newPage, ...callRecords.pages.slice(1)],
  };

  queryClient.setQueryData(recentCallRecordsQueryKey, newRecords);
}

export function updateCallIntelInsightInCache(
  callIntelInsight: CallRecordUpdateEvent['callIntelInsight'],
  queryClient: QueryClient,
  recentCallRecordsQueryKey: (string | string[] | undefined)[]
) {
  const callRecords = queryClient.getQueryData(recentCallRecordsQueryKey) as InfiniteData<RecentCallRecords>;

  if (!callRecords) {
    return;
  }

  const updatedPages = callRecords.pages.map((page) => {
    const updatedData = page.data.map((record) => {
      if (record.channelId === callIntelInsight?.channelId) {
        return {
          ...record,
          callIntelInsights: callIntelInsight,
        };
      }
      return record;
    });

    return {
      ...page,
      data: updatedData,
      rows: updatedData,
    };
  });

  const updatedData: InfiniteData<RecentCallRecords> = {
    ...callRecords,
    pages: updatedPages,
  };

  queryClient.setQueryData(recentCallRecordsQueryKey, updatedData);
}

export function updateFollowupTaskCountInCache(
  params: CallRecordUpdateEvent['tasksCount'],
  queryClient: QueryClient,
  recentCallRecordsQueryKey: (string | string[] | undefined)[]
) {
  if (!params?.channelId) return;

  const callRecords = queryClient.getQueryData(recentCallRecordsQueryKey) as InfiniteData<RecentCallRecords>;
  if (!callRecords) return;

  const updatedPages = callRecords.pages.map((page) => {
    const updatedData = page.data.map((record) => {
      if (record.channelId === params.channelId) {
        return {
          ...record,
          taskCount: params.count ?? 0,
        };
      }
      return record;
    });

    return {
      ...page,
      data: updatedData,
      rows: updatedData,
    };
  });

  const updatedData: InfiniteData<RecentCallRecords> = {
    ...callRecords,
    pages: updatedPages,
  };

  queryClient.setQueryData(recentCallRecordsQueryKey, updatedData);
}

function doesCallRecordPassFilterChecks(
  callRecord: CallRecord | undefined,
  appliedFilter: CallRecordsFiltersType | undefined
): boolean {
  if (!appliedFilter) {
    // if no filter is applied, then return true
    return true;
  }
  if (!callRecord) {
    return false;
  }
  if (appliedFilter.locationIds.length != 0 && !appliedFilter.locationIds.includes(callRecord.locationId ?? '')) {
    return false;
  }
  if (appliedFilter.callDirection && appliedFilter.callDirection != callRecord.direction) {
    return false;
  }
  if (!callRecord.status || (appliedFilter.status.length != 0 && !appliedFilter.status.includes(callRecord.status))) {
    return false;
  }
  if (
    appliedFilter.startDate &&
    callRecord.startedAt &&
    new Date(callRecord.startedAt) < new Date(formatStartDateWithTimeZone(appliedFilter.startDate))
  ) {
    return false;
  }
  if (
    appliedFilter.endDate &&
    callRecord.startedAt &&
    new Date(callRecord.startedAt) > new Date(formatEndDateWithTimeZone(appliedFilter.endDate))
  ) {
    return false;
  }

  if (
    (appliedFilter.officePhoneNumbers?.length ?? 0) > 0 &&
    !appliedFilter.officePhoneNumbers?.some(
      (phoneNumber) =>
        // phone number formats can be different depending on if countryCode is present or not
        // so check the last 10 digits of the phone number
        phoneNumber.slice(-10) === callRecord.officeNumber.slice(-10)
    )
  ) {
    return false;
  }

  if (appliedFilter.phoneNumber != '' || appliedFilter.name != '') {
    // if either phone number or name filter is applied, skip the call record
    return false;
  }

  if ((appliedFilter.aiOutcomes?.length ?? 0) > 0) {
    const recordAiOutcome = callRecord.aiMetadata?.aiOutcome;
    if (!recordAiOutcome || !appliedFilter.aiOutcomes?.includes(recordAiOutcome)) {
      return false;
    }
  }

  const hasHandledByFilter =
    appliedFilter.aiReceptionistHandled === true ||
    (appliedFilter.handledByUserIds?.length ?? 0) > 0 ||
    appliedFilter.allOfficeUsers === true;

  if (hasHandledByFilter) {
    const isAiHandled = !!callRecord.aiMetadata;
    const officeUserId = callRecord.officeUserId;

    const passesAiFilter = appliedFilter.aiReceptionistHandled === true && isAiHandled;
    const passesAllUsersFilter = appliedFilter.allOfficeUsers === true && !!officeUserId;
    const passesSpecificUserFilter =
      (appliedFilter.handledByUserIds?.length ?? 0) > 0 &&
      !!officeUserId &&
      !!appliedFilter.handledByUserIds?.includes(officeUserId);

    if (!passesAiFilter && !passesAllUsersFilter && !passesSpecificUserFilter) {
      return false;
    }
  }

  return true;
}

export const updateCallRecordTagsInRecentCalls = ({
  channelId,
  tagIds,
  mappedTags,
  queryClient,
  recentCallRecordsQueryKey,
}: {
  channelId: string;
  tagIds: string[];
  mappedTags: Record<string, Tag>;
  queryClient: QueryClient;
  recentCallRecordsQueryKey: (string | string[] | undefined)[];
}) => {
  const callRecords = queryClient.getQueryData(recentCallRecordsQueryKey) as InfiniteData<RecentCallRecords>;
  if (!callRecords) {
    return;
  }

  const updatedPages = callRecords.pages.map((page) => {
    const updatedData = page.data.map((record) => {
      if (record.channelId === channelId) {
        const updatedTagsIds =
          record.tagIds && record.tagIds.length > 0 ? Array.from(new Set([...record.tagIds, ...tagIds])) : tagIds;
        const updatedTags = updatedTagsIds.map((tagId) => mappedTags[tagId]);
        return {
          ...record,
          tagIds: updatedTagsIds,
          tags: updatedTags,
        };
      }
      return record;
    });

    return {
      ...page,
      data: updatedData,
    };
  });

  const updatedData: InfiniteData<RecentCallRecords> = {
    ...callRecords,
    pages: updatedPages,
  };
  queryClient.setQueryData(recentCallRecordsQueryKey, updatedData);
};
